import { ReactElement, ReactNode, useMemo } from 'react';
import { HiArrowsUpDown } from 'react-icons/hi2';
import {
  baselineConfig,
  configOptionLabel,
  destinationSessions,
  SessionConfigChoice,
  sessionOptionLabel,
} from '../utils/sessionConfigChoice';

/**
 * Where a run should go: a session, then one of that session's configs by
 * letter. One pair of dropdowns for every place that asks — the run list's
 * "Move to Session" and the recordings page's import destination — so both
 * offer the same sessions in the same order and label a config the same way.
 *
 * `sessions` is the `with_configs_min` payload (`useSessionsWithConfigs`): each
 * session carries its configs, which is what fills the second dropdown.
 */

interface SessionConfigPickerProps {
  sessions?: any[] | null;
  sessionsLoading?: boolean;
  value: SessionConfigChoice;
  /**
   * Fired with the whole choice. Picking a session moves the config to that
   * session's baseline; picking a config leaves the session as it was.
   */
  onChange: (choice: SessionConfigChoice) => void;
  sessionLabel?: string;
  configLabel?: string;
  /** Rendered beside the config dropdown: the button that acts on the choice. */
  action?: ReactNode;
  disabled?: boolean;
}

const selectClass = 'cr-rounded cr-border cr-border-gray-300 cr-bg-white cr-px-2 cr-py-1 cr-text-xs';

export default function SessionConfigPicker({
  sessions,
  sessionsLoading,
  value,
  onChange,
  sessionLabel = 'Session:',
  configLabel = 'Config:',
  action,
  disabled,
}: SessionConfigPickerProps): ReactElement | null {
  const targets = useMemo(() => destinationSessions(sessions), [sessions]);
  const { session, config } = value;

  if (targets.length === 0) {
    if (!sessionsLoading) return null;
    return (
      <div className="cr-px-4 cr-py-2 cr-text-sm cr-text-gray-400">
        <div className="cr-flex cr-items-center cr-gap-2">
          <HiArrowsUpDown className="cr-size-4" />
          <span>Loading sessions…</span>
        </div>
      </div>
    );
  }

  const pickSession = (sessionId: string) => {
    const next = targets.find((s: any) => s.id === sessionId) ?? null;
    onChange({ session: next, config: baselineConfig(next) });
  };

  const pickConfig = (configId: string) => {
    onChange({ session, config: session?.configs?.find((c: any) => c.id === configId) ?? null });
  };

  return (
    <>
      <div className="cr-px-4 cr-py-2 cr-text-sm cr-text-gray-700">
        <div className="cr-flex cr-items-center cr-gap-2">
          <HiArrowsUpDown className="cr-size-4" />
          <span>{sessionLabel}</span>
        </div>
        <select
          aria-label={sessionLabel}
          className={`cr-ml-4 cr-w-11/12 ${selectClass}`}
          value={session?.id || ''}
          disabled={disabled}
          onChange={(e) => pickSession(e.target.value)}
        >
          <option value="">Select a Session</option>
          {targets.map((s: any) => (
            <option key={s.id} value={s.id}>
              {sessionOptionLabel(s)}
            </option>
          ))}
        </select>
      </div>
      {session && (
        <div className="cr-px-4 cr-py-2 cr-text-sm cr-text-gray-700">
          <div className="cr-flex cr-items-center cr-gap-2">
            <HiArrowsUpDown className="cr-size-4" />
            <span>{configLabel}</span>
          </div>
          <div className="cr-ml-4 cr-flex cr-w-11/12 cr-items-center cr-gap-2">
            <select
              aria-label={configLabel}
              className={`cr-min-w-0 cr-flex-1 ${selectClass}`}
              value={config?.id || ''}
              disabled={disabled}
              onChange={(e) => pickConfig(e.target.value)}
            >
              <option value="">Select a Config</option>
              {session.configs?.map((c: any) => (
                <option key={c.id} value={c.id}>
                  {configOptionLabel(c)}
                </option>
              ))}
            </select>
            {action}
          </div>
        </div>
      )}
    </>
  );
}
